import { useMemo, useState } from 'react';
import { GripVertical, Search, X } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Checkbox } from '@/components/ui/Checkbox';
import { useStepDragReorder } from '@/hooks/useStepDragReorder';
import { cn } from '@/lib/utils';
import { DEFAULT_METRICS, METRIC_GROUPS, METRICS, type MetricKey } from './flexCatalog';

/**
 * The Metrics Selector (R17).
 *
 * The draft lives here until Apply Metrics; Cancel and the close button drop
 * it. Mount it only while open so each opening starts from the applied set.
 */

export interface MetricsSelectorModalProps {
  selected: MetricKey[];
  onApply: (metrics: MetricKey[]) => void;
  onClose: () => void;
}

function move<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length || from === to) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

export function MetricsSelectorModal({ selected, onApply, onClose }: MetricsSelectorModalProps) {
  const [draft, setDraft] = useState<MetricKey[]>(() => [...selected]);
  const [search, setSearch] = useState('');

  const drag = useStepDragReorder<MetricKey>({ items: draft, onReorder: (next) => setDraft(next) });

  const needle = search.trim().toLowerCase();
  const groups = useMemo(
    () =>
      METRIC_GROUPS.map((g) => ({
        label: g.label,
        metrics: g.metrics.filter((m) => needle === '' || METRICS[m].label.toLowerCase().includes(needle)),
      })).filter((g) => g.metrics.length > 0),
    [needle],
  );

  const toggle = (key: MetricKey, checked: boolean) =>
    setDraft((prev) => (checked ? (prev.includes(key) ? prev : [...prev, key]) : prev.filter((m) => m !== key)));

  const selectAll = () => setDraft((prev) => [...prev, ...DEFAULT_METRICS.filter((m) => !prev.includes(m))]);

  const count = draft.length;

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="xl"
      title="Metrics Selector"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              onApply(draft);
              onClose();
            }}
          >
            Apply Metrics
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <section aria-labelledby="flex-metrics-panel">
          <h3 id="flex-metrics-panel" className="mb-2 text-sm font-semibold text-slate-900">
            Metrics
          </h3>
          <div className="relative mb-2">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              aria-label="Search metrics"
              placeholder="Search metrics…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded border border-slate-200 py-1.5 pl-8 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div className="mb-3 flex gap-2">
            <Button variant="ghost" size="xs" onClick={selectAll}>
              Select All
            </Button>
            <Button variant="ghost" size="xs" onClick={() => setDraft([])}>
              Deselect All
            </Button>
          </div>
          {groups.length === 0 ? (
            <p className="text-sm text-slate-500">No metrics match your search.</p>
          ) : (
            <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
              {groups.map((group) => (
                <fieldset key={group.label}>
                  <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {group.label}
                  </legend>
                  <div className="space-y-1.5">
                    {group.metrics.map((key) => (
                      <Checkbox
                        key={key}
                        id={`flex-metric-${key}`}
                        label={METRICS[key].label}
                        checked={draft.includes(key)}
                        onChange={(e) => toggle(key, e.target.checked)}
                      />
                    ))}
                  </div>
                </fieldset>
              ))}
            </div>
          )}
        </section>

        <section aria-labelledby="flex-columns-panel">
          <div className="mb-2 flex items-center justify-between gap-2">
            <h3 id="flex-columns-panel" className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              Columns
              <Badge variant="info" size="sm">
                {count === 1 ? '1 Metric' : `${count} Metrics`}
              </Badge>
            </h3>
            <Button variant="ghost" size="xs" onClick={() => setDraft([])} disabled={count === 0}>
              Remove All
            </Button>
          </div>
          {count === 0 ? (
            <p className="text-sm text-slate-500">No columns chosen.</p>
          ) : (
            <ol aria-label="Columns" className="space-y-1" onDrop={drag.handleDrop}>
              {draft.map((key, index) => {
                const label = METRICS[key].label;
                return (
                  <li
                    key={key}
                    onDragOver={(e) => drag.handleDragOver(e, index)}
                    onDragEnter={drag.handleDragEnter}
                    onDragLeave={drag.handleDragLeave}
                    className={cn(
                      'flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm',
                      drag.dragIndex === index && 'opacity-50',
                      drag.dropIndex === index && 'border-t-2 border-t-blue-500',
                      drag.dropIndex === index + 1 && index === draft.length - 1 && 'border-b-2 border-b-blue-500',
                    )}
                  >
                    <button
                      type="button"
                      draggable
                      aria-label={`Move ${label}`}
                      title={`Move ${label}`}
                      onDragStart={(e) => drag.handleDragStart(e, index, e.currentTarget.parentElement)}
                      onDragEnd={drag.handleDragEnd}
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                          e.preventDefault();
                          setDraft((prev) => move(prev, index, e.key === 'ArrowUp' ? index - 1 : index + 1));
                        }
                      }}
                      className="cursor-grab rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <GripVertical className="h-4 w-4" />
                    </button>
                    <span className="flex-1">{label}</span>
                    <button
                      type="button"
                      aria-label={`Remove ${label}`}
                      title={`Remove ${label}`}
                      onClick={() => setDraft((prev) => prev.filter((m) => m !== key))}
                      className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </li>
                );
              })}
            </ol>
          )}
        </section>
      </div>
    </Modal>
  );
}
